/**
 * Compass - HUD compass showing cardinal directions relative to camera rotation.
 */

import * as THREE from 'three';

import { BaseUIComponent } from './BaseUIComponent.js';

import type { BaseUIComponentOptions, UIComponentMetadata } from './BaseUIComponent.js';

export interface CompassOptions extends BaseUIComponentOptions {
  width?: number;
  height?: number;
  pixelsPerDegree?: number;
}

export interface CompassDirection {
  name: string;
  angle: number;
  cardinal: boolean;
}

export class Compass extends BaseUIComponent<CompassOptions> {
  public static metadata: UIComponentMetadata = {
    displayName: 'Compass',
    category: 'navigation',
    summary: 'HUD compass showing cardinal directions relative to camera rotation.',
    useCases: ['compass', 'direction', 'heading', 'navigation', 'cardinal'],
    optionsType: 'CompassOptions',
    assetPaths: {
      template: '@engine/assets/ui/templates/compass.html',
      styles: '@engine/assets/ui/styles/compass.css',
    },
  };

  private compassMarkers: HTMLElement[] = [];

  private directions: CompassDirection[] = [
    { name: 'N', angle: 0, cardinal: true },
    { name: 'NE', angle: 45, cardinal: false },
    { name: 'E', angle: 90, cardinal: true },
    { name: 'SE', angle: 135, cardinal: false },
    { name: 'S', angle: 180, cardinal: true },
    { name: 'SW', angle: 225, cardinal: false },
    { name: 'W', angle: 270, cardinal: true },
    { name: 'NW', angle: 315, cardinal: false }
  ];

  protected getAssetPaths(): { templatePath: string; stylesPath: string } {
    return {
      templatePath: '@engine/assets/ui/templates/compass.html',
      stylesPath: '@engine/assets/ui/styles/compass.css'
    };
  }

  protected getDefaultOptions(): Required<CompassOptions> {
    return {
      position: 'bottom-center',
      visible: true,
      customClasses: [],
      customStyles: {},
      width: 300,
      height: 20,
      pixelsPerDegree: 2.5
    };
  }

  protected getInitialData(): Record<string, any> {
    return {
      position: this.options.position,
      width: this.options.width,
      height: this.options.height
    };
  }

  protected override async onInitialize(): Promise<void> {
    // Initialize compass markers
    this.initializeMarkers();
    // Update center line height based on compass height
    this.updateCenterLineHeight();
  }

  // Public API methods
  public updateRotation(camera: THREE.Camera): void {
    if (!camera || this.compassMarkers.length === 0) return;

    // Get the camera's world direction
    const direction = new THREE.Vector3();
    camera.getWorldDirection(direction);

    // Calculate angle in radians (0 = North, positive = clockwise)
    const angle = Math.atan2(direction.x, direction.z);

    // Convert to degrees and normalize to 0-360
    let playerAngle = (angle * 180 / Math.PI + 360) % 360;

    // Update each marker position
    const compassCenter = this.options.width / 2;

    this.compassMarkers.forEach(marker => {
      const markerAngle = parseFloat(marker.getAttribute('data-angle') ?? '0');

      // Calculate the relative angle difference
      let angleDiff = markerAngle - playerAngle;

      // Normalize angle difference to -180 to 180
      while (angleDiff > 180) angleDiff -= 360;
      while (angleDiff < -180) angleDiff += 360;

      // Convert angle to pixel position
      const xPosition = compassCenter + (angleDiff * this.options.pixelsPerDegree);

      // Only show markers that are within the visible area (with some padding)
      if (xPosition >= -50 && xPosition <= this.options.width + 50) {
        marker.style.left = `${xPosition}px`;
        marker.style.display = 'block';

        // Fade out markers near the edges for a smooth effect
        const distanceFromCenter = Math.abs(xPosition - compassCenter);
        const maxDistance = this.options.width / 2;
        const opacity = Math.max(0.3, 1 - (distanceFromCenter / maxDistance));
        marker.style.opacity = opacity.toString();
      } else {
        marker.style.display = 'none';
      }
    });
  }

  // Private methods
  private initializeMarkers(): void {
    const compassContainer = this.layout?.querySelector('.compass-markers') as HTMLElement;
    if (!compassContainer) return;

    // Create marker elements
    this.directions.forEach(dir => {
      const marker = document.createElement('div');
      marker.className = `compass-marker${dir.cardinal ? ' cardinal' : ''}`;
      marker.textContent = dir.name;
      marker.setAttribute('data-angle', dir.angle.toString());
      compassContainer.appendChild(marker);
      this.compassMarkers.push(marker);
    });
  }

  private updateCenterLineHeight(): void {
    const centerLine = this.layout?.querySelector('.compass-center-line') as HTMLElement;
    if (centerLine && this.options.height) {
      // Scale center line height to be about 20% of compass height, but at least 20px
      const lineHeight = Math.max(20, this.options.height * 0.2);
      centerLine.style.height = `${lineHeight}px`;
    }
  }

  protected override onDestroy(): void {
    // Clean up marker references
    this.compassMarkers = [];
  }
}